import { LitElement, html, css } from 'lit';

/**
 * <searchable-select> — a click-to-open dropdown with a search box that filters
 * the options (combobox), for long codelists like the ~290 BZB types where the
 * old `<input list>`/datalist autocomplete confused users.
 *
 * Usage:
 *   <searchable-select
 *     .options=${['hout', 'puin', …]}
 *     .value=${current}
 *     placeholder="Kies of zoek…"
 *     @change=${e => handle(e.detail.value)}>
 *   </searchable-select>
 *
 * Emits a `change` CustomEvent with `{ detail: { value } }` (value is '' when
 * cleared). The panel is rendered position:fixed and anchored to the trigger so
 * it never gets clipped by scrolling/overflow parents.
 */
export class SearchableSelect extends LitElement {
  static properties = {
    options: { type: Array },
    value: { type: String },
    placeholder: { type: String },
    deletable: { type: Array },   // option values that get a × delete button → emits `delete`
    addLabel: { type: String },   // optional footer action (e.g. "+ Nieuwe…") → emits `add`
    optionAction: { type: String }, // tooltip; when set, each option shows a gear → emits `action`
    allowCustom: { type: Boolean }, // allow committing a typed value that isn't in the list
    _open: { state: true },
    _query: { state: true },
    _active: { state: true },
    _pos: { state: true },
  };

  constructor() {
    super();
    this.options = [];
    this.value = '';
    this.placeholder = 'Selecteer…';
    this.deletable = [];
    this.addLabel = '';
    this.optionAction = '';
    this.allowCustom = false;
    this._open = false;
    this._query = '';
    this._active = 0;
    this._pos = null;
    this._onDocPointer = (e) => { if (!e.composedPath().includes(this)) this._close(); };
    this._onReposition = () => { if (this._open) this._close(); };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._removeListeners();
  }

  /** A group-header marker: `{ header: 'Label' }` renders as a non-selectable heading. */
  _isHeader(o) { return o != null && typeof o === 'object' && 'header' in o; }
  /** An option can be a plain string, or `{ value, label }` when they differ. */
  _optValue(o) { return typeof o === 'string' ? o : o.value; }
  _optLabel(o) { return typeof o === 'string' ? o : (o.label ?? o.value); }

  get _filtered() {
    const q = (this._query || '').trim().toLowerCase();
    const opts = this.options || [];
    // While searching, drop headers and only keep options whose label matches.
    return q ? opts.filter(o => !this._isHeader(o) && this._optLabel(o).toLowerCase().includes(q)) : opts;
  }

  /** Display label for the currently selected value (falls back to the raw value). */
  get _selectedLabel() {
    const found = (this.options || []).find(o => !this._isHeader(o) && this._optValue(o) === this.value);
    return found ? this._optLabel(found) : this.value;
  }

  /** First selectable (non-header) index in the current filtered list. */
  _firstSelectable() {
    const i = this._filtered.findIndex(o => !this._isHeader(o));
    return i < 0 ? 0 : i;
  }

  /** Next selectable index in the given direction, skipping headers; clamps at the ends. */
  _step(dir) {
    const items = this._filtered;
    let i = this._active;
    for (let n = 0; n < items.length; n++) {
      i += dir;
      if (i < 0 || i >= items.length) return this._active;
      if (!this._isHeader(items[i])) return i;
    }
    return this._active;
  }

  _toggle() { this._open ? this._close() : this._openPanel(); }

  _openPanel() {
    const rect = this.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const openUp = below < 240 && above > below;
    const maxHeight = Math.max(160, Math.min(320, (openUp ? above : below) - 12));
    this._pos = {
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      top: openUp ? null : Math.round(rect.bottom + 2),
      bottom: openUp ? Math.round(window.innerHeight - rect.top + 2) : null,
      maxHeight: Math.round(maxHeight),
    };
    this._query = '';
    this._active = this._firstSelectable();
    this._open = true;
    document.addEventListener('pointerdown', this._onDocPointer, true);
    window.addEventListener('scroll', this._onReposition, true);
    window.addEventListener('resize', this._onReposition);
    this.updateComplete.then(() => this.renderRoot.querySelector('.ss-search')?.focus());
  }

  _close() {
    if (!this._open) return;
    this._open = false;
    this._removeListeners();
  }

  _removeListeners() {
    document.removeEventListener('pointerdown', this._onDocPointer, true);
    window.removeEventListener('scroll', this._onReposition, true);
    window.removeEventListener('resize', this._onReposition);
  }

  _select(opt) {
    // opt may be an option item (string / {value,label}) or a raw value string (clear/custom).
    const value = (opt && typeof opt === 'object') ? this._optValue(opt) : opt;
    this.value = value;
    this.dispatchEvent(new CustomEvent('change', { detail: { value }, bubbles: true, composed: true }));
    this._close();
  }

  /** When allowCustom and the query isn't already an exact option, it can be committed verbatim. */
  get _customValue() {
    const q = (this._query || '').trim();
    if (!this.allowCustom || !q) return null;
    return (this.options || []).some(o => !this._isHeader(o) && this._optValue(o).toLowerCase() === q.toLowerCase()) ? null : q;
  }

  _onSearchKeydown(e) {
    const items = this._filtered;
    if (e.key === 'ArrowDown') { e.preventDefault(); this._active = this._step(1); this._scrollActive(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this._active = this._step(-1); this._scrollActive(); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[this._active];
      if (it != null && !this._isHeader(it)) this._select(it);
      else if (this._customValue) this._select(this._customValue);
    }
    else if (e.key === 'Escape') { e.preventDefault(); this._close(); }
  }

  _scrollActive() {
    this.updateComplete.then(() => this.renderRoot.querySelector('.ss-opt.is-active')?.scrollIntoView({ block: 'nearest' }));
  }

  _panelStyle() {
    const p = this._pos || {};
    const parts = [`left:${p.left}px`, `width:${p.width}px`, `max-height:${p.maxHeight}px`];
    if (p.top != null) parts.push(`top:${p.top}px`);
    if (p.bottom != null) parts.push(`bottom:${p.bottom}px`);
    return parts.join(';');
  }

  render() {
    const items = this._filtered;
    return html`
      <div part="trigger" class="ss-trigger ${this._open ? 'is-open' : ''}" @click=${this._toggle} title=${this._selectedLabel || ''}>
        <span class="ss-value ${this.value ? '' : 'is-placeholder'}">${this.value ? this._selectedLabel : this.placeholder}</span>
        <span class="ss-caret"></span>
      </div>
      ${this._open ? html`
        <div class="ss-panel" style=${this._panelStyle()}>
          <input class="ss-search" type="text" placeholder="Zoeken…"
            .value=${this._query}
            @input=${e => { this._query = e.target.value; this._active = this._firstSelectable(); }}
            @keydown=${this._onSearchKeydown}>
          <div class="ss-list">
            ${this.value ? html`<div class="ss-opt ss-clear" @click=${() => this._select('')}>— wissen —</div>` : ''}
            ${this._customValue ? html`<div class="ss-opt ss-custom" @click=${() => this._select(this._customValue)}>Gebruik "${this._customValue}"</div>` : ''}
            ${items.length === 0 && !this._customValue
              ? html`<div class="ss-empty">Geen resultaten</div>`
              : items.length === 0
              ? ''
              : items.map((o, i) => this._isHeader(o) ? html`
                  <div class="ss-group-header">${o.header}</div>
                ` : html`
                  <div class="ss-opt ${i === this._active ? 'is-active' : ''} ${this._optValue(o) === this.value ? 'is-selected' : ''}"
                    @mouseenter=${() => { this._active = i; }}
                    @click=${() => this._select(o)}>
                    <span class="ss-opt-label">${this._optLabel(o)}</span>
                    ${this.optionAction ? html`
                      <button class="ss-act" title=${this.optionAction}
                        @click=${(e) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('action', { detail: { value: this._optValue(o) }, bubbles: true, composed: true })); }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                      </button>` : ''}
                    ${(this.deletable || []).includes(this._optValue(o)) ? html`
                      <button class="ss-del" title="Categorie verwijderen"
                        @click=${(e) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('delete', { detail: { value: this._optValue(o) }, bubbles: true, composed: true })); }}>×</button>
                    ` : ''}
                  </div>
                `)}
          </div>
          ${this.addLabel ? html`
            <div class="ss-opt ss-add" @click=${() => { this._close(); this.dispatchEvent(new CustomEvent('add', { bubbles: true, composed: true })); }}>${this.addLabel}</div>
          ` : ''}
        </div>
      ` : ''}
    `;
  }

  static styles = css`
    :host { display: block; position: relative; }
    /* De aanroeper kan de knop bijstellen via ::part(trigger); de maten hieronder zijn de
       standaard voor korte codelijsten. */
    .ss-trigger {
      display: flex; align-items: center; justify-content: space-between; gap: 6px;
      /* 30px: dezelfde hoogte als een <select> of invoerveld in een formulier, zodat een
         keuzelijst met zoekfunctie er niet uitspringt naast een gewone. Pagina's die een
         andere maat willen, zetten die via ::part(trigger). */
      padding: 6px 8px; min-height: 30px; box-sizing: border-box;
      border: 1px solid var(--mv-border); border-radius: 4px;
      /* De aanroeper kan de vulling zetten met --ss-background; in een venster krijgen de
         velden een lichte vulling zodat te zien is wat invulbaar is. Open is hij wit, net als
         een gewoon veld met focus. */
      background: var(--ss-background, var(--mv-surface)); color: var(--mv-text);
      font-size: 0.85rem; cursor: pointer;
    }
    .ss-trigger.is-open { border-color: var(--mv-primary); background: var(--mv-surface); }
        /* Codelijstwaarden staan in kleine letters in de bron en lezen beter met een hoofdletter.
       Voor waarden waar hoofdlettergebruik betekenis heeft, zoals een e-mailadres, zet de
       aanroeper --ss-text-transform op none. */
    .ss-value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      text-transform: var(--ss-text-transform, capitalize); }
    .ss-value.is-placeholder { color: var(--mv-text-muted); text-transform: none; }
    /* Hetzelfde driehoekje als een <select> in het laagformulier: dat tekent er een van 8x5 in
       dezelfde grijstint. Een ▾ als tekstteken kwam er dunner en kleiner uit, en dan lijkt een
       zoekbare keuzelijst een ander soort veld dan een gewone. */
    .ss-caret {
      flex: none; width: 8px; height: 5px;
      background: var(--mv-text-muted);
      clip-path: polygon(0 0, 100% 0, 50% 100%);
    }
    .ss-panel {
      position: fixed; z-index: 10000; display: flex; flex-direction: column;
      background: var(--mv-surface); border: 1px solid var(--mv-border);
      border-radius: 6px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18); overflow: hidden;
    }
    .ss-search {
      flex: none; border: none; border-bottom: 1px solid var(--mv-border);
      padding: 8px 10px; font-size: 0.85rem; outline: none;
      background: var(--mv-surface); color: var(--mv-text);
    }
    .ss-list { overflow-y: auto; }
    .ss-opt { display: flex; align-items: center; gap: 6px; padding: 7px 10px; font-size: 0.85rem; cursor: pointer; color: var(--mv-text); text-transform: var(--ss-text-transform, capitalize); }
    .ss-opt-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ss-opt.is-active { background: var(--mv-surface-alt); }
    .ss-opt.is-selected { font-weight: 600; }
    .ss-clear { color: var(--mv-text-muted); font-style: italic; text-transform: none; }
    .ss-add { border-top: 1px solid var(--mv-border); color: var(--mv-primary); font-weight: 600; text-transform: none; }
    .ss-custom { color: var(--mv-primary); font-weight: 600; text-transform: none; }
    .ss-del {
      flex: none; border: none; background: none; cursor: pointer; line-height: 1;
      font-size: 1rem; color: var(--mv-text-muted); padding: 0 4px; border-radius: 4px;
    }
    .ss-del:hover { color: var(--mv-danger, #f1416c); background: var(--mv-surface-alt); }
    .ss-act {
      flex: none; border: none; background: none; cursor: pointer; line-height: 0;
      display: inline-flex; color: var(--mv-text-muted); padding: 2px 4px; border-radius: 4px;
    }
    .ss-act:hover { color: var(--mv-primary); background: var(--mv-surface-alt); }
    .ss-empty { padding: 10px; font-size: 0.85rem; color: var(--mv-text-muted); }
    .ss-group-header {
      padding: 7px 10px 3px; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.05em;
      text-transform: uppercase; color: var(--mv-text-muted); cursor: default;
      position: sticky; top: 0; background: var(--mv-surface);
    }
  `;
}

customElements.define('searchable-select', SearchableSelect);
